import { createRoute, redirect } from '@tanstack/react-router';
import ParticipantDetailsPage from '@/features/dashboard/participants/pages/ParticipantDetailsPage.tsx';
import ParticipantTabMenu from '@/features/dashboard/participants/pages/ParticipantTabMenu.tsx';
import ParticipantInfoTab from '@/features/dashboard/participants/pages/ParticipantInfoTab.tsx';
import BookingBillTab from '@/features/dashboard/bookingChanges/pages/BookingBillTab.tsx';
import ParticipantDetailsSkeleton from '@/features/dashboard/participants/components/ParticipantDetailsSkeleton.tsx';
import DetailsErrorPage from '@/features/dashboard/bookingChanges/components/DetailsErrorPage.tsx';
import { appRoute } from '@/features/dashboard/_root/routes/appRoute.tsx';
import { isMobileViewport } from '@/hooks/use-mobile.tsx';
import { queryClient } from '@/App.tsx';
import { getParticipantDetailsQueryOptions } from '@/features/dashboard/participants/hooks/useGetParticipantDetails.ts';
import { getClientDetailsQueryOptions } from '@/features/dashboard/clients/hooks/useGetClientDetails.ts';
import { getTourQueryOptions } from '@/features/dashboard/tours/hooks/useGetTour.ts';
import { getBookingBillQueryOptions } from '@/features/dashboard/bookings/hooks/useGetBookingBill.ts';
import { getBookingDetailsQueryOptions } from '@/features/dashboard/bookings/hooks/useGetBookingDetails.ts';
import { getOpenDraftQueryOptions } from '@/features/dashboard/bookingChanges/hooks/useGetOpenDraft.ts';
import { detailsSearchSchema } from '@/features/dashboard/bookingChanges/routes/detailsSearch.ts';
import { ParticipantDetailsTab } from '@/features/dashboard/bookingChanges/routes/detailsTabs.ts';

/**
 * Ekran uczestnika. Route nadrzędny to szkielet — nagłówek, panel boczny
 * i modale; każda zakładka ma własny segment ścieżki i własny route potomny.
 *
 * `?mode=` zostaje parametrem wyszukiwania i siedzi tutaj, bo tryb edycji
 * dotyczy całego ekranu — zakładki dziedziczą go razem z resztą kontekstu.
 *
 * Loader dociąga też rachunek, szkic i metryczkę rezerwacji, więc nieudane
 * pobranie kończy się `errorComponent`, a nie pustym stanem w środku zakładki.
 */
export const participantRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/participants/$participantId',
  component: ParticipantDetailsPage,
  validateSearch: detailsSearchSchema,
  loader: async ({ params }) => {
    const { participantId } = params;

    const participant = await queryClient.fetchQuery(
      getParticipantDetailsQueryOptions(participantId),
    );

    // Cały ekran stoi na rezerwacji — bez niej nie ma czego pokazać, więc
    // brak `bookingId` kończy się `errorComponent`, a nie pustymi kartami.
    const { bookingId } = participant;
    if (!bookingId) {
      throw new Error(
        `Uczestnik ${participantId} nie należy do żadnej rezerwacji`,
      );
    }

    const [client, tour] = await Promise.all([
      queryClient.fetchQuery(
        getClientDetailsQueryOptions(participant.clientId),
      ),
      queryClient.fetchQuery(getTourQueryOptions(participant.tourId)),
      queryClient.ensureQueryData(getBookingBillQueryOptions(bookingId)),
      queryClient.ensureQueryData(getOpenDraftQueryOptions(bookingId)),
      queryClient.ensureQueryData(getBookingDetailsQueryOptions(bookingId)),
    ]);

    return {
      participant,
      client,
      tour,
      bookingId,
      billParticipantId: participantId,
    };
  },
  pendingComponent: ParticipantDetailsSkeleton,
  errorComponent: DetailsErrorPage,
});

/**
 * Adres bez segmentu zakładki. Tak nawigują wiersze tabel i tak wyglądają
 * linki sprzed tej zmiany.
 *
 * Szeroko prowadzi na „Informacje”. Poniżej `md` jest osobnym stanem — ekranem
 * z kaflami zakładek — więc przekierowania tam nie ma; zabrałoby jedyny adres,
 * pod który da się z zakładki wrócić.
 */
export const participantIndexRoute = createRoute({
  getParentRoute: () => participantRoute,
  path: '/',
  beforeLoad: ({ params, search }) => {
    if (isMobileViewport()) return;

    throw redirect({
      to: '/app/participants/$participantId/info',
      params,
      search,
      replace: true,
    });
  },
  component: ParticipantTabMenu,
});

export const participantInfoRoute = createRoute({
  getParentRoute: () => participantRoute,
  path: ParticipantDetailsTab.INFO,
  component: ParticipantInfoTab,
});

export const participantBookingRoute = createRoute({
  getParentRoute: () => participantRoute,
  path: ParticipantDetailsTab.BOOKING,
  component: BookingBillTab,
});
